import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, makeStyles, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import _, { isArray } from 'lodash';
import moment from 'moment';
import { useSelector } from 'react-redux';
import SearchInput from '../../../components/SearchInput';
import { i18n } from '../../../language';
import StyleButton from '../../../components/StyleButton';
import { AddIcon, DocIcon, EditIcon, GraphIcon } from '../../../components/Icon';
import AutoCompleteMultiCheckbox from '../../../components/AutoCompleteMultiCheckbox';
import TableUI from '../../../components/TableUI';
import { EQUIPMENT_STATUS_ENUM } from '../../../comm/constant';
import { equipmentAPI } from '../../../apis';
import { downloadFileById, initValue } from '../../../utils';
import { authNameE } from '../../../utils/userFuncs';
import useUpdateEffect from '../../../utils/useUpdateEffect';
import Sort from '../../../components/Sort';
import PieChart from './PieChart/index.js';
import EditEquipment from './EditEquipment';
import RegisterEquipment from './RegisterEquipment';

const useStyle = makeStyles(() => ({
  root: {
    borderRadius: '0 0.937rem 0.937rem',
    backgroundColor: '#ffffff',
    padding: '1rem 2rem 0 1.875rem',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  headText: {
    fontSize: '1rem',
    fontWeight: '500',
    fontFamily: 'NotoSans-Medium',
    color: '#3e3e3e',
    marginRight: '0.5rem',
  },
  graphButton: {
    width: '3.5rem',
    height: '3rem',
    border: 'solid 1px #538fff',
    borderRadius: '10px',
    backgroundColor: '#f5faff',
  },
  graphIcon: {
    fontSize: '1.5rem',
    fill: 'none',
    stroke: '#538FFF',
    strokeWidth: 2,
  },
  defaultButton: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#1973e6',
    textDecoration: 'underline',
  },
  tableStatus: {
    width: '0.625rem',
    height: '0.625rem',
    minWidth: '0.625rem',
    minHeight: '0.625rem',
    borderRadius: '50%',
    backgroundColor: '#34c759',
    marginRight: '0.625rem',
  },
  editButton: {
    backgroundColor: '#4c93ff',
    borderRadius: '0.625rem',
    width: '2.25rem',
    height: '2.25rem',
  },
  editIcon: {
    fontSize: '1rem',
    color: '#ffffff',
  },
  sortButton: {
    width: '1.5rem',
    height: '1.5rem',
    marginLeft: '0.5rem',
    borderRadius: '12px',
    backgroundColor: '#f5faff',
  },
}));

const initFilter = {
  keyword: '',
  categoryIds: [],
  locationIds: [],
  status: [],
};

const initPageInfo = {
  page: 1,
  size: 200,
};

const currentLocationFormat = (item) => {
  // 如果当前设备是借出去的状态,并且api有返回roomName的话就要拼上roomName显示
  if ([2, 3, 4, 5].includes(item?.status)) {
    return `${item.hospCode}-${item.location}${item.roomName ? `-${item.roomName}` : ''}`;
  } else {
    if (item.returnDesignateRoom) {
      return `${item.hospCode}-${item.returnDesignateRoom}`;
    } else {
      return `${item.hospCode}-${item.location}${item.satelliteStoresName ? ` (${item.satelliteStoresName})` : ''}`;
    }
  }
};

const ManageEquipmentTab = ({ refresh, setStopRefresh }) => {
  const classes = useStyle({});
  const [equipmentList, setEquipmentList] = useState({});
  const loginInfo = useSelector((state) => state.loginInfo);
  const [sort, setSort] = useState({});
  const [filter, setFilter] = useState(initFilter);
  const [inputRest, setInputReset] = useState(false);
  const [categories, setCategories] = useState();
  const [requestWard, setRequestWard] = useState([]);
  const [pieOpen, setPieOpen] = useState(false);
  const [editData, setEditData] = useState();
  const [register, setRegister] = useState(false);
  const roleMap = useSelector((state) => state.roleMap);
  const [pageInfo, setPageInfo] = useState(initPageInfo);
  const [loading, setLoading] = useState(false);

  // 處理 表單API 和 export API 的參數
  const tableParamsHandler = useMemo(
    () => ({
      keyword: filter.keyword,
      categoryIds: _.map(filter.categoryIds, (item) => item.value),
      locationKeys: _.map(filter.locationIds, (item) => item.value),
      status: _.map(filter.status, (item) => item.value),
      wardId: loginInfo.wardId,
      sortInfo: sort,
      pageInfo: {
        pageNo: pageInfo.page,
        pageSize: pageInfo.size,
      },
    }),
    [filter, sort, loginInfo.wardId, pageInfo]
  );

  // 刷新頁面
  useUpdateEffect(() => {
    setPageInfo({ ...initPageInfo });
  }, [refresh]);

  // export button 事件
  const exportHandler = useCallback(async () => {
    try {
      await downloadFileById(
        () =>
          equipmentAPI.getEquipmentExport({
            search: tableParamsHandler,
            desc: {
              hospName: loginInfo.hospitalName,
              wardName: loginInfo.wardName,
              locationNames: _.map(filter.requestWard, (item) => item.text),
              categoryNames: _.map(filter.category, (item) => item.text),
              statusNames: _.map(filter.status, (item) => item.text),
            },
          }),
        () => `WardEquipment Manager Equipment Export Excel_${moment().valueOf()}`
      );
    } catch (error) {
      console.log(error);
    }
  }, [tableParamsHandler, loginInfo, filter]);

  // filter事件
  const filterHandler = (type) => (value) => {
    // 初始化翻頁信息
    setPageInfo(initPageInfo);

    // 處理相應的filter事件
    if (type === 'reset') {
      setFilter(initFilter);
      setInputReset((preState) => !preState);
      setSort({});
    } else {
      setFilter((preFilter) => ({ ...preFilter, [type]: value }));
    }
  };

  // 表格的排序點擊事件
  const changeSortHandler = (type) => () => {
    // 初始化翻頁信息
    setPageInfo(initPageInfo);

    setSort((preSort) => {
      if (preSort.sortKey === type) {
        return { ...preSort, sortOrder: preSort.sortOrder === 'desc' ? 'asc' : 'desc' };
      } else {
        return { sortKey: type, sortOrder: 'desc' };
      }
    });
  };

  // 獲取 category list
  const getCategoryList = useCallback(async () => {
    try {
      const { result } = await equipmentAPI.getCategoryList({ hospitalId: loginInfo.hospitalId });

      setCategories(result.categories);
    } catch (error) {
      console.log(error);
    }
  }, [loginInfo]);

  // 獲取 ward list
  const getWardList = useCallback(async () => {
    try {
      const { result } = await equipmentAPI.getEquipmentItem({ wardId: loginInfo.wardId });
      setRequestWard(result);
    } catch (error) {
      console.log(error);
    }
  }, [loginInfo]);

  useEffect(() => {
    (async () => {
      await getCategoryList();
    })();
    // (async () => {
    //   await getWardList();
    // })();
  }, []);

  // filter dropdown 的 list item
  const itemLists = useMemo(() => {
    let statusList = _.map(EQUIPMENT_STATUS_ENUM, (item) => ({
      text: i18n.t(item.textKey),
      value: item.enumKey,
      tagId: item.enumKey,
    }));
    let wardList = [];
    let categoryList = [];

    if (_.isArray(requestWard)) {
      wardList = _.map(requestWard, (item) => ({ text: item, value: item }));
    }

    if (_.isArray(categories)) {
      categoryList = _.map(categories, (item) => ({
        text: item.categoryName,
        value: item.categoryId,
        tagId: item.categoryId,
      }));
    }

    return { wardList, statusList, categoryList };
  }, [requestWard, categories, i18n.t()]);

  // 獲取表格數據
  const getEquipmentListAPI = useCallback(async () => {
    try {
      setLoading(true);
      const { result } = await equipmentAPI.getEquipmentList(tableParamsHandler, { isHideLoading: true });
      setLoading(false);

      if (pageInfo.page === 1) {
        setEquipmentList(result);
      } else {
        setEquipmentList((preData) => ({ ...result, data: [...preData.data, ...result.data] }));
      }
      // filter的location下拉的数据
      if (Array.isArray(result?.locationList)) {
        setRequestWard(result?.locationList);
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  }, [tableParamsHandler]);

  useEffect(() => {
    (async () => {
      await getEquipmentListAPI();
    })();
  }, [getEquipmentListAPI]);

  const editHandler = useCallback(
    (props) => () => {
      if (props) {
        if (roleMap[authNameE.equipment_manage_Equipment_of_own_ward_create_update]) {
          setEditData(props);
          setStopRefresh(Boolean(props));
        }
      } else {
        setEditData();
        setStopRefresh(Boolean(props));
      }
    },
    [roleMap[authNameE.equipment_manage_Equipment_of_own_ward_create_update]]
  );

  const editSuccessHandler = useCallback(() => {
    setEditData();
    setStopRefresh(false);
    setPageInfo({ ...initPageInfo });
  }, [getEquipmentListAPI]);

  const registerSuccessHandler = useCallback(() => {
    setRegister(false);
    setStopRefresh(false);
    setPageInfo({ ...initPageInfo });
  }, [getEquipmentListAPI]);

  // 格式化表格數據
  const tableData = useMemo(() => {
    let data = [];

    if (equipmentList) {
      if (isArray(equipmentList?.data)) {
        data = _.map(equipmentList.data, (item) => ({
          assetId: item.assetId ?? '',
          localCode: item.localCode ?? '',
          category: item.categoryName ?? '',
          model: item.model ?? '',
          currentLocation: item?.location,
          remark: item?.remark,
          status: item.status,
          statusData: _.find(EQUIPMENT_STATUS_ENUM, (statusItem) => statusItem.enumKey === item.status),
        }));
      }
    }

    return { data };
  }, [equipmentList]);

  // 餅圖 Pop up 開關
  const pieChartHandler = useCallback(
    (props) => () => {
      setPieOpen(props);
      setStopRefresh(props);
    },
    []
  );

  const registerHandler = useCallback(
    (props) => () => {
      setRegister(props);
      setStopRefresh(props);
    },
    []
  );

  // 翻頁
  const turnPages = useCallback(() => {
    setPageInfo((preInfo) => {
      if (preInfo.page * preInfo.size < equipmentList.count) {
        return {
          ...preInfo,
          page: preInfo.page + 1,
        };
      } else return preInfo;
    });
  }, [equipmentList]);

  return (
    <div className={classes.root}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb="0.8rem">
        <SearchInput
          width="30.625rem"
          height="2.563rem"
          placeholder={i18n.t('EQUIPMENT_MANAGE_SEARCH_PLACEHOLDER')}
          inputID="equipment_search_input"
          buttonID="equipment_search_button"
          clearID="equipment_search_clear"
          onChange={filterHandler('keyword')}
          reset={inputRest}
          closeIcon={true}
        />
        <Box display="flex" alignItems="center" gridGap="0.8rem">
          <ButtonBase
            id="equipment-button-pie"
            width="3.5rem"
            height="3rem"
            className={classes.graphButton}
            onClick={pieChartHandler(true)}
          >
            <GraphIcon className={classes.graphIcon} />
          </ButtonBase>
          {roleMap[authNameE.equipment_export_and_Reports] && (
            <StyleButton
              id="equipment-button-export"
              text={i18n.t('EQUIPMENT_MANAGE_EXPORT')}
              Icon={DocIcon}
              iconStyle={{ fontSize: '1.25rem', color: '#538fff' }}
              height="3rem"
              onClick={exportHandler}
            />
          )}
          {roleMap[authNameE.equipment_manage_Equipment_of_own_ward_create_update] && (
            <StyleButton
              id="equipment-button-register"
              type="fill"
              text={i18n.t('EQUIPMENT_MANAGE_REGISTER_EQUIPMENT')}
              Icon={AddIcon}
              iconStyle={{ fontSize: '1rem', color: '#ffffff' }}
              width="12.75rem"
              height="3rem"
              onClick={registerHandler(true)}
            />
          )}
        </Box>
      </Box>
      <Box display="flex" alignItems="center">
        <Box display="flex" alignItems="center" mb="0.8rem" mr="0.8rem">
          <div className={classes.headText}>{i18n.t('EQUIPMENT_MANAGE_FILTER')}</div>
          <AutoCompleteMultiCheckbox
            dropDownIcon={true}
            align="right"
            height="2.563rem"
            width="12.5rem"
            errMsgMargin="3px 0 0 0.825rem"
            errMsgWidth="11rem"
            allText={i18n.t('EQUIPMENT_MANAGE_CATEGORY')}
            itemList={itemLists.categoryList}
            value={filter.categoryIds}
            onChange={filterHandler('categoryIds')}
            type="equipment"
            id="equipment-autocomplete-category"
          />
        </Box>
        <Box mb="0.8rem" mr="0.8rem">
          <AutoCompleteMultiCheckbox
            dropDownIcon={true}
            align="right"
            height="2.563rem"
            width="12.5rem"
            errMsgMargin="3px 0 0 0.825rem"
            errMsgWidth="11rem"
            allText={i18n.t('EQUIPMENT_MANAGE_LOCATION')}
            itemList={itemLists.wardList}
            value={filter.locationIds}
            onChange={filterHandler('locationIds')}
            type="equipment"
            id="equipment-autocomplete-location"
          />
        </Box>
        <Box mb="0.8rem" mr="0.8rem">
          <AutoCompleteMultiCheckbox
            dropDownIcon={true}
            align="right"
            height="2.563rem"
            width="12.5rem"
            errMsgMargin="3px 0 0 0.825rem"
            errMsgWidth="11rem"
            allText={i18n.t('EQUIPMENT_MANAGE_STATUS')}
            itemList={itemLists.statusList}
            value={filter.status}
            onChange={filterHandler('status')}
            type="equipment"
            id="equipment-autocomplete-status"
            onlySelect={true}
          />
        </Box>
        <Box mb="0.8rem">
          <ButtonBase id="equipment-button-reset" className={classes.defaultButton} onClick={filterHandler('reset')}>
            {i18n.t('EQUIPMENT_MANAGE_RESET')}
          </ButtonBase>
        </Box>
      </Box>
      <TableUI turnPages={turnPages} loading={loading}>
        <TableHead>
          <TableRow>
            <TableCell align="left" style={{ width: '8%' }}>
              {i18n.t('EQUIPMENT_MANAGE_TABLE_ASSET')}
              <ButtonBase
                id="equipment-button-sort-assetId"
                className={classes.sortButton}
                onClick={changeSortHandler('asset')}
              >
                <Sort sort={sort} sortKey="asset" />
              </ButtonBase>
            </TableCell>
            <TableCell align="left" style={{ width: '11%' }}>
              {i18n.t('EQUIPMENT_MANAGE_TABLE_LOCATION')}
              <ButtonBase
                id="equipment-button-sort-localCode"
                className={classes.sortButton}
                onClick={changeSortHandler('code')}
              >
                <Sort sort={sort} sortKey="code" />
              </ButtonBase>
            </TableCell>
            <TableCell align="left" style={{ width: '17%' }}>
              <Box display="flex" alignItems="center">
                {i18n.t('EQUIPMENT_MANAGE_TABLE_CATEGORY')}
                <ButtonBase
                  id="equipment-button-sort-category"
                  className={classes.sortButton}
                  onClick={changeSortHandler('category')}
                >
                  <Sort sort={sort} sortKey="category" />
                </ButtonBase>
              </Box>
            </TableCell>
            <TableCell align="left" style={{ width: '16%' }}>
              <Box display="flex" alignItems="center">
                {i18n.t('EQUIPMENT_MANAGE_TABLE_MODEL')}
                <ButtonBase
                  id="equipment-button-sort-model"
                  className={classes.sortButton}
                  onClick={changeSortHandler('model')}
                >
                  <Sort sort={sort} sortKey="model" />
                </ButtonBase>
              </Box>
            </TableCell>
            <TableCell align="left" style={{ width: '16%' }}>
              <Box display="flex" alignItems="center">
                {i18n.t('EQUIPMENT_MANAGE_TABLE_CURRENT_LOCATION')}
                <ButtonBase
                  id="equipment-button-sort-location"
                  className={classes.sortButton}
                  onClick={changeSortHandler('location')}
                >
                  <Sort sort={sort} sortKey="location" />
                </ButtonBase>
              </Box>
            </TableCell>
            <TableCell align="left" style={{ width: '30%', minWidth: '10rem' }}>
              <Box display="flex" alignItems="center">
                {i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_REMARK')}
                <ButtonBase
                  id="equipment-button-sort-remarks"
                  className={classes.sortButton}
                  onClick={changeSortHandler('remarks')}
                >
                  <Sort sort={sort} sortKey="remarks" />
                </ButtonBase>
              </Box>
            </TableCell>
            <TableCell align="left" style={{ width: '19%', minWidth: '10rem' }}>
              {i18n.t('EQUIPMENT_MANAGE_TABLE_STATUS')}
              <ButtonBase
                id="equipment-button-sort-status"
                className={classes.sortButton}
                onClick={changeSortHandler('status')}
              >
                <Sort sort={sort} sortKey="status" />
              </ButtonBase>
            </TableCell>
            <TableCell align="left" style={{ width: '5%' }} />
          </TableRow>
        </TableHead>

        <TableBody>
          {tableData.data.map((item, index) => (
            <TableRow onDoubleClick={editHandler(item)} key={item.assetId}>
              <TableCell>
                <Box display="flex" alignItems="center">
                  {item.assetId}
                </Box>
              </TableCell>
              <TableCell>{item.localCode}</TableCell>
              <TableCell>{item.category}</TableCell>
              <TableCell>{item.model}</TableCell>
              <TableCell>{item.currentLocation}</TableCell>
              <TableCell>{item?.remark}</TableCell>
              <TableCell>
                {item.statusData ? (
                  <Box display="flex" alignItems="center">
                    <div className={classes.tableStatus} style={{ backgroundColor: item.statusData.color }} />
                    {i18n.t(item.statusData.textKey)}
                  </Box>
                ) : null}
              </TableCell>
              <TableCell>
                {roleMap[authNameE.equipment_manage_Equipment_of_own_ward_create_update] && (
                  <ButtonBase
                    id={`equipment-button-edit-${index}`}
                    className={classes.editButton}
                    onClick={editHandler(item)}
                  >
                    <EditIcon className={classes.editIcon} />
                  </ButtonBase>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </TableUI>
      {pieOpen && <PieChart openHandler={pieChartHandler} categoryIds={tableParamsHandler.categoryIds} />}
      {editData ? (
        <EditEquipment
          closeHandler={editHandler()}
          assetId={editData.assetId}
          editSuccessHandler={editSuccessHandler}
        />
      ) : null}
      {register && <RegisterEquipment closeHandler={registerHandler(false)} successHandler={registerSuccessHandler} />}
    </div>
  );
};

export default ManageEquipmentTab;
